import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import {debounce} from 'throttle-debounce';

type Options = Partial<SearchBoxOptions>;

export interface SearchBoxElement extends HTMLElement {
  searchbox?: SearchBox;
}

export interface SearchBoxOptions {
  activeClass: string;
  debounce: number;
  contentEl: string;
  fieldEl: string;
  triggerEl: string;
  closeEl: string;
  overlayEl: string;
  klevuSelector: string;
  klevuResults: string;
  selector: string;
}

export class SearchBox {
  static defaults: SearchBoxOptions = {
    activeClass: 'is-active',
    closeEl: '[data-searchbox-close]',
    contentEl: '[data-searchbox-content]',
    debounce: 300,
    fieldEl: '[data-searchbox-input]',
    klevuResults: '.klevu-fluid',
    klevuSelector: 'klevuSearchingArea',
    overlayEl: '[data-searchbox-overlay]',
    selector: '[data-searchbox]',
    triggerEl: '[data-searchbox-trigger]',
  };
  element: SearchBoxElement;
  options: SearchBoxOptions;
  content: HTMLElement;
  input: HTMLInputElement;
  closeBtn: HTMLButtonElement;
  overlayEl: HTMLDivElement;
  focusHandler: EventListener;
  blurHandler: EventListener;
  keyupHandler: EventListener;
  clickHandler: EventListener;

  constructor(element: SearchBoxElement, options: Options = {}) {
    if (!element || element.searchbox) {
      return;
    }

    element.searchbox = this;
    this.element = element;
    this.options = {
      ...SearchBox.defaults,
      ...this.element.dataset,
      ...options,
    };

    const content = this.element.querySelector(
      this.options.contentEl,
    ) as HTMLElement;

    const input = this.element.querySelector(
      this.options.fieldEl,
    ) as HTMLInputElement;

    const closeBtn = this.element.querySelector(
      this.options.closeEl,
    ) as HTMLButtonElement;

    const overlayEl = this.element.querySelector(
      this.options.overlayEl,
    ) as HTMLDivElement;

    if (!content || !input || !closeBtn) {
      return;
    }

    this.content = content;
    this.input = input;
    this.closeBtn = closeBtn;
    this.overlayEl = overlayEl;

    this.bind();
  }

  bind() {
    this.focusHandler = this.handleFocus.bind(this);
    this.blurHandler = this.handleBlur.bind(this);
    this.clickHandler = this.handleTriggerClick.bind(this);
    this.keyupHandler = debounce(
      this.options.debounce,
      this.handleKeyUp.bind(this),
    );

    this.content.addEventListener('focus', this.focusHandler, true);
    this.content.addEventListener('blur', this.blurHandler, true);
    document.addEventListener('click', this.clickHandler);
    document.addEventListener('keyup', this.keyupHandler);
  }

  handleKeyUp(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest(this.options.selector)) {
      return;
    }

    const newEvent = new Event('searchkeyup', {
      bubbles: true,
    });

    this.element.dispatchEvent(newEvent);
  }

  handleFocus(event: Event) {
    this.toggleField(event.target as HTMLElement, true);
  }

  handleBlur(event: Event) {
    this.toggleField(event.target as HTMLElement, false);
  }

  handleTriggerClick(event: Event) {
    const target = event.target as HTMLElement;

    if (
      !target.closest(this.options.triggerEl) &&
      !target.closest(this.options.closeEl) &&
      !target.closest(this.options.overlayEl)
    ) {
      return;
    }

    event.preventDefault();
    this.content.classList.toggle(this.options.activeClass);

    if (this.content.classList.contains(this.options.activeClass)) {
      this.input.focus();
      this.setScrollLock(true);
    } else {
      this.input.blur();
      this.setScrollLock(false);
    }
  }

  setScrollLock = (enableLock: boolean) => {
    const klevuEl = document.getElementById(this.options.klevuSelector);

    if (!klevuEl) {
      return;
    }

    if (enableLock) {
      disableBodyScroll(klevuEl);
    } else {
      enableBodyScroll(klevuEl);
    }
  };

  toggleField(element: HTMLElement, state: boolean) {
    const match = element.closest(this.options.fieldEl);

    if (!match) {
      return;
    }

    this.element.classList.toggle(this.options.activeClass, state);
  }

  destroy() {
    this.element.removeEventListener('focus', this.focusHandler, true);
    this.element.removeEventListener('blur', this.blurHandler, true);
    this.element.removeEventListener('click', this.clickHandler);
    this.element.removeEventListener('keyup', this.keyupHandler);

    this.element.searchbox = undefined;
  }
}
